import { useEffect, useLayoutEffect, useMemo, useState, useRef, useCallback, useTransition } from 'react';
import type { UIEvent } from 'react';
import { Button, Input, Spin, Tag, message } from 'antd';
import type {
  ClientEnvironmentDTO,
  ConfigurationComparisonDTO,
  ComparisonResultData,
  FolderResult,
  MismatchDetail,
  DifferenceItem,
} from '../../../infrastructure/models';
import { getAllClientEnvironments, getComparisonDetail, getComparisonResultData } from '../../../infrastructure/apiClient';
import { useClients } from '../../../context/ClientContext';
import { buildEnvironmentNameMap, getEnvironmentName } from '../../../utils/environmentUtils';
import type { FilterValue, FlatDifference } from './comparisonResultTypes';
import EnvironmentPane from './EnvironmentPane';
import FolderNavigator from './FolderNavigator';
import CompareModal from './CompareModal';
import styles from './ComparisonResult.module.scss';

interface ComparisonResultProps {
  comparisonId?: string | null;
  comparison?: ConfigurationComparisonDTO | null;
  onBack: () => void;
}

const getDiffKey = (m: MismatchDetail): string =>
  `${m.configName}-${m.configSubElementName}-${m.differenceType}`;

const getDiffItemKey = (d: DifferenceItem): string =>
  `${d.configName}-entry-${d.differenceType}`;

const ComparisonResult = ({ comparisonId: comparisonIdProp, comparison, onBack }: ComparisonResultProps) => {
  const { activeClient } = useClients();

  const [data, setData] = useState<ConfigurationComparisonDTO | null>(null);
  const [resultData, setResultData] = useState<ComparisonResultData | null>(null);
  const [loading, setLoading] = useState(false);
  const [resultLoading, setResultLoading] = useState(false);
  const [environments, setEnvironments] = useState<ClientEnvironmentDTO[]>([]);

  const [selectedFolderIndex, setSelectedFolderIndex] = useState(0);
  const [activeDifferenceIndex, setActiveDifferenceIndex] = useState(0);
  const [filterValue, setFilterValue] = useState<FilterValue>('all');
  const [appliedFilter, setAppliedFilter] = useState<FilterValue>('all');
  const [, startTransition] = useTransition();
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [compareModalRecord, setCompareModalRecord] = useState<MismatchDetail | null>(null);

  const leftPaneRef = useRef<HTMLDivElement | null>(null);
  const rightPaneRef = useRef<HTMLDivElement | null>(null);
  const scrollingSourceRef = useRef<'A' | 'B' | null>(null);

  const comparisonId = comparisonIdProp ?? comparison?.id;

  useEffect(() => {
    setData(null);
    setResultData(null);
    setResultLoading(false);
    setSelectedFolderIndex(0);
    setActiveDifferenceIndex(0);
    setFilterValue('all');
    setAppliedFilter('all');
    setSearchQuery('');
    setCollapsedSections(new Set());
    setCompareModalRecord(null);

    if (!activeClient?.id || !comparisonId) return;

    const controller = new AbortController();
    setLoading(true);
    getComparisonDetail(activeClient.id, comparisonId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setData(response);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load comparison detail', err);
          message.error('Failed to load comparison detail.');
          setData(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [activeClient?.id, comparisonId]);

  useEffect(() => {
    if (!activeClient?.id || !comparisonId || !data) return;
    if (data.status !== 'Completed') return;

    const controller = new AbortController();
    setResultLoading(true);
    getComparisonResultData(activeClient.id, comparisonId, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setResultData(result);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load comparison results', err);
          message.error('Failed to load comparison results.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setResultLoading(false);
      });
    return () => controller.abort();
  }, [activeClient?.id, comparisonId, data?.status]);

  useEffect(() => {
    if (!activeClient?.id) {
      setEnvironments([]);
      return;
    }
    const controller = new AbortController();
    getAllClientEnvironments(activeClient.id, { offset: 0, limit: 1000, signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) setEnvironments(response?.data ?? []);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load environments', err);
          setEnvironments([]);
        }
      });
    return () => controller.abort();
  }, [activeClient?.id]);

  const environmentMap = useMemo(() => buildEnvironmentNameMap(environments), [environments]);
  const envAName = getEnvironmentName(data?.environmentA ?? comparison?.environmentA, environmentMap);
  const envBName = getEnvironmentName(data?.environmentB ?? comparison?.environmentB, environmentMap);

  const folderResults = resultData?.folderResults ?? [];
  const safeFolderIndex = selectedFolderIndex < folderResults.length ? selectedFolderIndex : 0;
  const selectedFolder: FolderResult | null = folderResults[safeFolderIndex] ?? null;
  const summary = resultData?.summary ?? data?.summary ?? null;

  const matchesSearch = useCallback((m: MismatchDetail): boolean => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (m.configName?.toLowerCase().includes(q) ?? false)
      || (m.configSubElementName?.toLowerCase().includes(q) ?? false);
  }, [searchQuery]);

  const matchesDiffItemSearch = useCallback((d: DifferenceItem): boolean => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return d.configName?.toLowerCase().includes(q) ?? false;
  }, [searchQuery]);

  const filteredMismatches = useMemo(() => {
    if (!selectedFolder) return [];
    return selectedFolder.mismatchDetails.filter((m) => {
      if (appliedFilter === 'Matched') return m.differenceType === 'Matching';
      if (appliedFilter !== 'all' && m.differenceType !== appliedFilter) return false;
      return matchesSearch(m);
    });
  }, [selectedFolder, appliedFilter, matchesSearch]);

  const filteredDiffItems = useMemo(() => {
    if (!selectedFolder) return [];
    return selectedFolder.differenceItems.filter((d) => {
      if (appliedFilter === 'Matched') return d.differenceType === 'Matching';
      if (d.mismatchCount > 0) return false;
      if (appliedFilter !== 'all' && d.differenceType !== appliedFilter) return false;
      return matchesDiffItemSearch(d);
    });
  }, [selectedFolder, appliedFilter, matchesDiffItemSearch]);

  const flatDifferences = useMemo<FlatDifference[]>(() => {
    const diffs: FlatDifference[] = [];
    folderResults.forEach((folder, fi) => {
      if (folder.status === 'Failed') return;
      if (appliedFilter === 'Matched') {
        if (folder.status !== 'Matching') return;
      } else if (folder.status === 'Matching') {
        return;
      }

      folder.differenceItems
        .filter(d => {
          if (appliedFilter === 'Matched') return d.differenceType === 'Matching';
          if (d.mismatchCount > 0) return false;
          return appliedFilter === 'all' || d.differenceType === appliedFilter;
        })
        .forEach((d) => {
          diffs.push({ folderIndex: fi, mismatchIndex: -1, key: getDiffItemKey(d) });
        });

      folder.mismatchDetails
        .filter(m => {
          if (appliedFilter === 'Matched') return m.differenceType === 'Matching';
          return appliedFilter === 'all' || m.differenceType === appliedFilter;
        })
        .forEach((m, mi) => {
          diffs.push({ folderIndex: fi, mismatchIndex: mi, key: getDiffKey(m) });
        });
    });
    return diffs;
  }, [folderResults, appliedFilter]);

  const currentDifference = flatDifferences[activeDifferenceIndex] ?? null;
  const activeDifferenceKey = currentDifference?.key;

  const scrollContainerToElement = (container: HTMLDivElement, element: HTMLElement) => {
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const currentScrollTop = container.scrollTop;
    const elementTopInsideContainer = elementRect.top - containerRect.top + currentScrollTop;
    const targetScrollTop = elementTopInsideContainer - container.clientHeight / 2 + element.clientHeight / 2;
    container.scrollTo({ top: Math.max(targetScrollTop, 0), behavior: 'smooth' });
  };

  const scrollToDifferenceKey = (key: string) => {
    [leftPaneRef, rightPaneRef].forEach((ref) => {
      if (!ref.current) return;
      const target = ref.current.querySelector<HTMLElement>(`[data-diff-key="${key}"]`);
      if (target) scrollContainerToElement(ref.current, target);
    });
  };

  const handleSelectFolder = (index: number) => {
    setSelectedFolderIndex(index);
    setCollapsedSections(new Set());
    setSearchQuery('');
    setFilterValue('all');
    setAppliedFilter('all');
    setActiveDifferenceIndex(0);
    if (leftPaneRef.current) leftPaneRef.current.scrollTop = 0;
    if (rightPaneRef.current) rightPaneRef.current.scrollTop = 0;
  };

  const handleSyncedScroll = (source: 'A' | 'B') => (_event: UIEvent<HTMLDivElement>) => {
    const sourceRef = source === 'A' ? leftPaneRef : rightPaneRef;
    const targetRef = source === 'A' ? rightPaneRef : leftPaneRef;

    if (scrollingSourceRef.current && scrollingSourceRef.current !== source) return;
    scrollingSourceRef.current = source;

    if (sourceRef.current && targetRef.current) {
      targetRef.current.scrollTop = sourceRef.current.scrollTop;
    }

    requestAnimationFrame(() => {
      scrollingSourceRef.current = null;
    });
  };

  const handleToggleSection = (configName: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(configName)) {
        next.delete(configName);
      } else {
        next.add(configName);
      }
      return next;
    });
  };

  const handleFilterChange = (value: FilterValue) => {
    setFilterValue(value);
    startTransition(() => {
      setAppliedFilter(value);
    });
    setActiveDifferenceIndex(0);
    if (leftPaneRef.current) leftPaneRef.current.scrollTop = 0;
    if (rightPaneRef.current) rightPaneRef.current.scrollTop = 0;
  };

  const folderStats = useMemo(() => {
    if (!selectedFolder) return { total: 0, modified: 0, onlyInSource: 0, onlyInTarget: 0, unmatched: 0 };
    const allMismatches = selectedFolder.mismatchDetails;
    const allDiffItems = selectedFolder.differenceItems;
    const modified = allMismatches.filter(m => m.differenceType === 'Modified').length;
    const onlyInSource = allMismatches.filter(m => m.differenceType === 'Removed').length
      + allDiffItems.filter(d => d.differenceType === 'Removed').length;
    const onlyInTarget = allMismatches.filter(m => m.differenceType === 'Added').length
      + allDiffItems.filter(d => d.differenceType === 'Added').length;
    const unmatched = allMismatches.filter(m => m.differenceType === 'Unmatched').length;
    const total = modified + onlyInSource + onlyInTarget + unmatched;
    return { total, modified, onlyInSource, onlyInTarget, unmatched };
  }, [selectedFolder]);

  useLayoutEffect(() => {
    const left = leftPaneRef.current;
    const right = rightPaneRef.current;
    if (!left || !right) return;

    const leftRows = left.querySelectorAll<HTMLElement>('[data-diff-key]');
    const rightRows = right.querySelectorAll<HTMLElement>('[data-diff-key]');

    leftRows.forEach(el => { el.style.minHeight = ''; });
    rightRows.forEach(el => { el.style.minHeight = ''; });

    const heights: { left: HTMLElement; right: HTMLElement; max: number }[] = [];
    leftRows.forEach(leftRow => {
      const key = leftRow.getAttribute('data-diff-key');
      if (!key) return;
      const rightRow = right.querySelector<HTMLElement>(`[data-diff-key="${CSS.escape(key)}"]`);
      if (!rightRow) return;
      const max = Math.max(leftRow.offsetHeight, rightRow.offsetHeight);
      if (leftRow.offsetHeight !== rightRow.offsetHeight) {
        heights.push({ left: leftRow, right: rightRow, max });
      }
    });

    heights.forEach(({ left: l, right: r, max }) => {
      l.style.minHeight = `${max}px`;
      r.style.minHeight = `${max}px`;
    });
  }, [filteredMismatches, filteredDiffItems, collapsedSections, safeFolderIndex]);

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <h1 className={styles.title}>Comparison Results</h1>
        <Button onClick={onBack} type="default">
          Back
        </Button>
      </header>

      {loading && (
        <div className={styles.loadingState}>
          <Spin size="large" />
        </div>
      )}

      {!loading && !data && (
        <div className={styles.emptyState}>No comparison data available.</div>
      )}

      {!loading && data && (
        <>
          {/* Meta bar */}
          <div className={styles.meta}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Name</span>
              <span className={styles.metaValue}>{data.name ?? '—'}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Description</span>
              <span className={styles.metaValueSmall}>{data.description ?? '—'}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Source</span>
              <span className={styles.metaValue}>{envAName}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Target</span>
              <span className={styles.metaValue}>{envBName}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Status</span>
              <span className={styles.metaValue}>
                <Tag color={data.status === 'Completed' ? 'green' : data.status === 'Failed' ? 'red' : 'gold'}>
                  {data.status}
                </Tag>
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Completed At</span>
              <span className={styles.metaValueMedium}>
                {resultData?.completedAt ? new Date(resultData.completedAt).toLocaleString() : '—'}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Run By</span>
              <span className={styles.metaValue}>{data.createdBy ?? '—'}</span>
            </div>
          </div>

          {/* Summary stat cards */}
          {summary && (
            <div className={styles.summaryCards}>
              <button
                type="button"
                className={`${styles.statCard} ${styles.statCardClickable} ${filterValue === 'all' ? styles.statCardActive : ''}`}
                onClick={() => handleFilterChange('all')}
              >
                <span className={styles.statLabel}>Total Differences</span>
                <span className={styles.statValue}>{folderStats.total}</span>
              </button>
              <button
                type="button"
                className={`${styles.statCard} ${styles.statModified} ${styles.statCardClickable} ${filterValue === 'Modified' ? styles.statCardActive : ''}`}
                onClick={() => handleFilterChange('Modified')}
              >
                <span className={styles.statLabel}>Changed</span>
                <span className={styles.statValue}>{folderStats.modified}</span>
              </button>
              <button
                type="button"
                className={`${styles.statCard} ${styles.statRemoved} ${styles.statCardClickable} ${filterValue === 'Removed' ? styles.statCardActive : ''}`}
                onClick={() => handleFilterChange('Removed')}
              >
                <span className={styles.statLabel}>Only in {envAName}</span>
                <span className={styles.statValue}>{folderStats.onlyInSource}</span>
              </button>
              <button
                type="button"
                className={`${styles.statCard} ${styles.statAdded} ${styles.statCardClickable} ${filterValue === 'Added' ? styles.statCardActive : ''}`}
                onClick={() => handleFilterChange('Added')}
              >
                <span className={styles.statLabel}>Only in {envBName}</span>
                <span className={styles.statValue}>{folderStats.onlyInTarget}</span>
              </button>
            </div>
          )}

          {resultLoading && (
            <div className={styles.loadingState}>
              <Spin size="default" />
              <span>Loading comparison results…</span>
            </div>
          )}

          {!resultLoading && resultData && (
            <>
              <div className={styles.panelToolbar}>
                {selectedFolder && (
                  <span className={styles.activeFolderLabel}>
                    Viewing: {selectedFolder.displayName ?? selectedFolder.folderName}
                  </span>
                )}
                <Input
                  size="small"
                  placeholder="Search differences..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  allowClear
                  className={styles.paneSearchInput}
                />
              </div>
              <div className={styles.panelLayout}>
                <EnvironmentPane
                  side="A"
                  folder={selectedFolder}
                  filteredMismatches={filteredMismatches}
                  filteredDiffItems={filteredDiffItems}
                  activeDifferenceKey={activeDifferenceKey}
                  scrollRef={leftPaneRef}
                  onScroll={handleSyncedScroll('A')}
                  collapsedSections={collapsedSections}
                  onToggleSection={handleToggleSection}
                  onShowMore={setCompareModalRecord}
                  envAName={envAName}
                  envBName={envBName}
                />

                <EnvironmentPane
                  side="B"
                  folder={selectedFolder}
                  filteredMismatches={filteredMismatches}
                  filteredDiffItems={filteredDiffItems}
                  activeDifferenceKey={activeDifferenceKey}
                  scrollRef={rightPaneRef}
                  onScroll={handleSyncedScroll('B')}
                  collapsedSections={collapsedSections}
                  onToggleSection={handleToggleSection}
                  onShowMore={setCompareModalRecord}
                  envAName={envAName}
                  envBName={envBName}
                />

                <FolderNavigator
                  folderResults={folderResults}
                  selectedFolderIndex={safeFolderIndex}
                  onSelectFolder={handleSelectFolder}
                  filterValue={filterValue}
                  onFilterChange={handleFilterChange}
                  envAName={envAName}
                  envBName={envBName}
                />
              </div>
            </>
          )}

          {data.errorMessage && (
            <p className={styles.errorState}>Error: {data.errorMessage}</p>
          )}
          {resultData?.errorMessage && (
            <p className={styles.errorState}>Error: {resultData.errorMessage}</p>
          )}
        </>
      )}

      <CompareModal
        record={compareModalRecord}
        envAName={envAName}
        envBName={envBName}
        onClose={() => setCompareModalRecord(null)}
      />
    </div>
  );
};

export default ComparisonResult;
